import { Spinner } from "@/components/ui/spinner";
import { useState } from "react";
import { X } from "lucide-react";
import { ExpenseCategory, RecurringFrequency } from "@boo/shared";
import type { RecurringExpenseSuggestionItem } from "@boo/shared";
import { useRecurringExpenseSuggestionStore } from "../store/useRecurringExpenseSuggestionStore";
import AmountInput from "@/components/AmountInput";

const CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  [ExpenseCategory.FOOD]: "Food",
  [ExpenseCategory.TRANSPORT]: "Transport",
  [ExpenseCategory.HOUSING]: "Housing",
  [ExpenseCategory.ENTERTAINMENT]: "Entertainment",
  [ExpenseCategory.HEALTH]: "Health",
  [ExpenseCategory.FINANCIAL]: "Financial",
  [ExpenseCategory.INVESTMENT]: "Investment",
  [ExpenseCategory.SHOPPING]: "Shopping",
  [ExpenseCategory.BILLS]: "Bills",
  [ExpenseCategory.OTHER]: "Other",
};

const EXPENSE_CATEGORIES = [
  ...Object.values(ExpenseCategory).filter((c) => c !== ExpenseCategory.OTHER),
  ExpenseCategory.OTHER,
] as ExpenseCategory[];

const FREQ_OPTIONS = [
  { value: RecurringFrequency.WEEKLY, label: "Weekly" },
  { value: RecurringFrequency.MONTHLY, label: "Monthly" },
  { value: RecurringFrequency.QUARTERLY, label: "Quarterly" },
  { value: RecurringFrequency.YEARLY, label: "Yearly" },
] as const;

const INPUT_CLASS =
  "w-full rounded-[8px] border-[0.5px] border-border bg-[var(--surface-muted)] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-border";

interface Props {
  suggestion: RecurringExpenseSuggestionItem;
  onClose: () => void;
}

export default function EditConfirmModal({ suggestion, onClose }: Props) {
  const { editAndConfirmSuggestion } = useRecurringExpenseSuggestionStore();

  const [title, setTitle] = useState(
    suggestion.displayName ?? suggestion.merchantName,
  );
  const [amount, setAmount] = useState(String(suggestion.averageAmount));
  const [category, setCategory] = useState<ExpenseCategory>(
    ExpenseCategory.OTHER,
  );
  const [frequency, setFrequency] = useState<RecurringFrequency>(
    suggestion.frequency as RecurringFrequency,
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!title.trim()) next.title = "Name is required.";
    const amt = parseFloat(amount);
    if (!amount || isNaN(amt) || amt <= 0)
      next.amount = "Amount must be greater than 0.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      await editAndConfirmSuggestion(suggestion.id, {
        title: title.trim(),
        amount: parseFloat(amount),
        category,
        frequency,
        dayOfMonth: suggestion.estimatedDayOfMonth ?? 1,
      });
      onClose();
    } catch {
      setErrors({ submit: "Failed to save. Please try again." });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-sm mx-4 rounded-[16px] bg-card border-[0.5px] border-border shadow-xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[15px] font-semibold text-foreground">
            Confirm recurring expense
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Name
            </label>
            <input
              className={INPUT_CLASS}
              placeholder="e.g. Netflix"
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            {errors.title && (
              <p className="text-[12px] text-red-500">{errors.title}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Amount (฿)
            </label>
            <AmountInput
              className={`${INPUT_CLASS} [font-variant-numeric:tabular-nums]`}
              placeholder="0"
              value={amount}
              onChange={setAmount}
            />
            {errors.amount && (
              <p className="text-[12px] text-red-500">{errors.amount}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Category
            </label>
            <select
              className={INPUT_CLASS}
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
            >
              {EXPENSE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {CATEGORY_LABELS[cat]}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] text-muted-foreground font-medium">
              Frequency
            </label>
            <div className="grid grid-cols-2 gap-2">
              {FREQ_OPTIONS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFrequency(value)}
                  className={`rounded-[8px] border-[0.5px] py-2 text-[12px] font-medium transition-colors cursor-pointer ${
                    frequency === value
                      ? "border-foreground bg-foreground text-[var(--page-bg)]"
                      : "border-border bg-[var(--surface-muted)] text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {errors.submit && (
            <p className="text-[12px] text-red-500">{errors.submit}</p>
          )}

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-1.5 w-full rounded-[8px] bg-foreground text-[var(--page-bg)] py-2.5 text-[13px] font-semibold hover:opacity-80 transition-opacity disabled:opacity-40 cursor-pointer"
          >
            {saving && <Spinner className="size-3.5" />}
            {saving ? "Saving…" : "Add as recurring expense"}
          </button>
        </form>
      </div>
    </div>
  );
}
